import { useEffect, useMemo, useState } from "react";
import Dropdown from "react-bootstrap/Dropdown";
import Form from "react-bootstrap/Form";
import { toast, ToastContainer } from "react-toastify";
import ReusableModal from "../../../components/atoms/modal/ReusableModal";
import { postCustomerRequestData } from "./CustomerRequestApi";
import { getLocations } from "../../Global Dashboard/GlobalDashboardApi";

const issueOptions = [
  { key: "isTechnicalIssue", label: "Technical Issues" },
  { key: "isQuestionAboutSample", label: "Questions around your sample(s)" },
  { key: "isEnvelopeRequest", label: "Request more envelopes" },
];

const ContactUs = ({ show, onHide, onSubmit }) => {
  const [selectedIssues, setSelectedIssues] = useState({});
  const [count, setCount] = useState(0);
  const [message, setMessage] = useState("");
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [locations, setLocations] = useState([]);

  const userData = useMemo(
    () => JSON.parse(localStorage.getItem("userData")) || {},
    []
  );
  const companyId = userData.companyId || 0;
  const locationId = userData.locationID || 0;
  const requestedBy = userData.userID || 0;

  // Fetch locations from API when modal opens
  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    const fetchLocations = async () => {
      try {
        const response = await getLocations();
        if (cancelled) return;
        const locs = Array.isArray(response?.results) ? response.results : [];
        setLocations(
          locs.map((loc) => ({
            value: String(loc.locationId || loc.id || ""),
            label: loc.name || loc.locationName || `Location ${loc.locationId || ""}`,
          }))
        );
      } catch (error) {
        console.error("Error fetching locations:", error);
        if (!cancelled) setLocations([]);
      }
    };
    fetchLocations();
    return () => { cancelled = true; };
  }, [show]);

  useEffect(() => {
    if (show) {
      setSelectedLocations([]);
    }
  }, [show]);

  const handleCheckboxChange = (key) => {
    setSelectedIssues((prev) => {
      const nextChecked = !prev[key];

      if (key === "isEnvelopeRequest" && !nextChecked) {
        setSelectedLocations([]);
      }

      return {
        ...prev,
        [key]: nextChecked,
      };
    });
  };

  const handleLocationToggle = (value) => {
    setSelectedLocations((prev) =>
      prev.includes(value) ? [] : [value]
    );
  };

  const handleSubmit = async () => {
    const isEnvelopeRequest = !!selectedIssues.isEnvelopeRequest;

    if (!message.trim()) {
      toast.warning("Please enter comments before submitting");
      return;
    }

    if (isEnvelopeRequest && selectedLocations.length === 0) {
      toast.warning("Please select at least one location");
      return;
    }

    const selectedLocationIds = selectedLocations
      .map((value) => Number(value))
      .filter((value) => !Number.isNaN(value));

    const selectedLocationLabels = locations
      .filter((loc) => selectedLocations.includes(String(loc.value)))
      .map((loc) => loc.label);

    const commentsParts = [];
    if (message.trim()) commentsParts.push(message.trim());
    if (selectedLocationLabels.length > 0) {
      commentsParts.push(`Location: ${selectedLocationLabels.join(", ")}`);
    }
    if (isEnvelopeRequest) commentsParts.push(`Envelope Count: ${count}`);

    const payload = {
      requestedBy,
      companyId,
      locationId: isEnvelopeRequest
        ? selectedLocationIds[0]
        : locationId,
      isTechnicalIssue: !!selectedIssues.isTechnicalIssue,
      isQuestionAboutSample: !!selectedIssues.isQuestionAboutSample,
      isEnvelopeRequest,
      isHistoricalData: false,
      comments: commentsParts.join(" | "),
    };
    await postCustomerRequestData(payload);
    toast.success("Request submitted successfully!");
    setSelectedIssues({});
    setCount(0);
    setMessage("");
    setSelectedLocations([]);
    onHide();
    if (onSubmit) onSubmit(payload);
  };

  return (
    <ReusableModal
      show={show}
      onHide={onHide}
      title="Contact Us"
      size="md"
      buttons={[
        { text: "Cancel", variant: "outlined", onClick: onHide },
        { text: "Submit", variant: "active", onClick: handleSubmit },
      ]}
    >
      <div>
        {issueOptions.map((option) => (
          <div key={option.key} className="mb-2 d-flex align-items-center">
            <input
              type="checkbox"
              checked={!!selectedIssues[option.key]}
              onChange={() => handleCheckboxChange(option.key)}
              style={{ marginRight: 8 }}
              title={option.label}
            />
            <span>{option.label}</span>
            {option.key === "isEnvelopeRequest" && (
              <div className="d-flex align-items-center ms-3">
                <span style={{ marginRight: 8 }}>Count</span>
                <input
                  type="number"
                  min={0}
                  value={count}
                  onChange={(e) => setCount(Number(e.target.value))}
                  title="Enter envelope count"
                  style={{
                    width: 60,
                    height: 32,
                    borderRadius: 4,
                    border: "1px solid #ced4da",
                    padding: "0 8px",
                  }}
                />
              </div>
            )}
          </div>
        ))}
        {selectedIssues.isEnvelopeRequest && (
          <div className="mt-3 d-flex align-items-center gap-2">
            <Form.Label className="mb-0" style={{ minWidth: 70 }}>
              Location
            </Form.Label>
            <Dropdown className="flex-grow-1">
              <Dropdown.Toggle
                variant="outline-secondary"
                style={{ width: "100%", textAlign: "left" }}
                id="location-multiselect"
                title="Select location"
              >
                {selectedLocations.length > 0
                  ? locations
                      .filter((loc) => selectedLocations.includes(String(loc.value)))
                      .map((loc) => loc.label)
                      .join(", ")
                  : "Select location"}
              </Dropdown.Toggle>
              <Dropdown.Menu style={{ width: "100%" }}>
                {locations.map((loc) => (
                  <Dropdown.Item
                    key={loc.value}
                    as="div"
                    style={{ padding: "4px 12px" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Form.Check
                      type="radio"
                      name="location-radio"
                      id={`location-checkbox-${loc.value}`}
                      label={loc.label}
                      checked={selectedLocations.includes(String(loc.value))}
                      onChange={() => handleLocationToggle(String(loc.value))}
                    />
                  </Dropdown.Item>
                ))}
                {locations.length === 0 && (
                  <Dropdown.Item as="div" className="text-muted" style={{ padding: "4px 12px" }}>
                    No locations available
                  </Dropdown.Item>
                )}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        )}
        <textarea
          className="form-control mt-3"
          rows={4}
          placeholder="Message here..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          title="Enter your message"
        />
      </div>
       <ToastContainer position="top-right" autoClose={3000} />
    </ReusableModal>
  );
};

export default ContactUs;